import {
  formatE164,
  partnerCategoryLabel,
  partnerDisplayName,
  type Job,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { navigate, useAsync } from "../../lib/hooks";
import {
  fetchPartners,
  fetchReferrals,
  insertReferral,
  type Partner,
} from "../../lib/partnerQueries";
import { useSession } from "../../session";
import { ReferralModal } from "../partners/ReferralModal";

/**
 * Where a job meets the partner network.
 *
 * Two questions, and the first one is the one that is worth money: **did
 * somebody send us this?** It has to be asked where the job is, on the day the
 * job is created, because a week later nobody remembers — and a referral
 * nobody recorded is a partner who never finds out their introduction turned
 * into a roof.
 *
 * Once it is recorded the job keeps it up to date by itself: reaching Sends
 * marks the referral won, and being marked lost marks it lost
 * (`referrals_follow_the_job`). Nobody has to remember twice.
 */
export function PartnersPanel({ job }: { job: Job }) {
  const { profileId } = useSession();
  const partnersQuery = useAsync(fetchPartners, []);
  const referralsQuery = useAsync(() => fetchReferrals(), []);

  const [linking, setLinking] = useState(false);
  const [partnerId, setPartnerId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referring, setReferring] = useState<Partner | null>(null);

  // Held steady across renders: `?? []` makes a new array each time, and the
  // lookup below takes it as a dependency.
  const partners = useMemo(
    () => partnersQuery.data ?? [],
    [partnersQuery.data],
  );

  const onThisJob = useMemo(
    () => (referralsQuery.data ?? []).filter((row) => row.job_id === job.id),
    [referralsQuery.data, job.id],
  );

  const byId = useMemo(() => {
    const map = new Map<string, Partner>();
    for (const partner of partners) map.set(partner.id, partner);
    return map;
  }, [partners]);

  async function link(): Promise<void> {
    if (!partnerId) return;
    setSaving(true);
    setError(null);
    try {
      await insertReferral({
        partner_id: partnerId,
        direction: "from_partner",
        // The name as it stands on the job today. Kept verbatim, because the
        // referral is a thing that was said on a particular day and the
        // customer record can be renamed or deleted afterwards.
        customer_name: job.contact?.full_name ?? "This customer",
        customer_phone: job.contact?.cell ?? job.contact?.phone ?? null,
        contact_id: job.contact_id,
        job_id: job.id,
        notes: note.trim() || null,
        // The job is already here, so the referral starts past "open".
        outcome: "contacted",
        created_by: profileId,
      });
      setLinking(false);
      setPartnerId("");
      setNote("");
      await referralsQuery.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="partners-panel">
      <ErrorNote error={error} />

      <h3>{t("Referred by a partner?")}</h3>

      {onThisJob.length === 0 && !linking && (
        <p className="sub">
          {t(
            "Nobody recorded yet. If a realtor, an agent or an adjuster sent this one our way, say so — it is how they find out it turned into a roof.",
          )}
        </p>
      )}

      {onThisJob.length > 0 && (
        <ul className="ref-list">
          {onThisJob.map((row) => {
            const partner = byId.get(row.partner_id);
            return (
              <li key={row.id}>
                <span className={`pill ${row.direction}`}>
                  {row.direction === "from_partner" ? t("→ us") : t("→ them")}
                </span>
                <button
                  className="linky"
                  onClick={() => navigate(`partner=${row.partner_id}`)}
                >
                  {partnerDisplayName(partner ?? {})}
                </button>
                <span className="sub">
                  {partner
                    ? ` · ${t(partnerCategoryLabel(partner.category))}`
                    : ""}
                  {" · "}
                  {t(row.outcome)}
                </span>
                {row.notes && <p className="sub">{row.notes}</p>}
              </li>
            );
          })}
        </ul>
      )}

      {linking ? (
        <div className="fieldgrid">
          <div className="field full">
            <label htmlFor="refpartner">{t("Who sent them")}</label>
            <select
              id="refpartner"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
            >
              <option value="">{t("Pick a partner")}</option>
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partnerDisplayName(partner)}
                  {partner.company ? ` — ${partner.company}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="field full">
            <label htmlFor="refnote">{t("Anything worth remembering")}</label>
            <input
              id="refnote"
              value={note}
              placeholder={t(
                "Called about the inspection report on the listing",
              )}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="rowacts">
            <button
              className="btn-small"
              disabled={!partnerId || saving}
              onClick={() => void link()}
            >
              {t("Record it")}
            </button>
            <button className="btn-small" onClick={() => setLinking(false)}>
              {t("Cancel")}
            </button>
          </div>
        </div>
      ) : (
        <div className="rowacts">
          <button className="btn-small" onClick={() => setLinking(true)}>
            {onThisJob.length > 0 ? t("Add another") : t("Yes — record it")}
          </button>
        </div>
      )}

      <h3 style={{ marginTop: 20 }}>{t("Refer this customer to a partner")}</h3>
      <p className="sub">
        {t(
          "A realtor, a lender, another trade. Opens Messages with the introduction ready to go from your own phone.",
        )}
      </p>
      <div className="fieldgrid">
        <div className="field full">
          <label htmlFor="refto">{t("Send them to")}</label>
          <select
            id="refto"
            defaultValue=""
            onChange={(e) => {
              const partner = byId.get(e.target.value);
              if (partner) setReferring(partner);
              e.target.value = "";
            }}
          >
            <option value="">{t("Pick a partner")}</option>
            {partners.map((partner) => (
              <option key={partner.id} value={partner.id}>
                {partnerDisplayName(partner)}
                {partner.company ? ` — ${partner.company}` : ""}
                {partner.phone_mobile
                  ? ` (${formatE164(partner.phone_mobile)})`
                  : ` (${t("no mobile")})`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {referring && (
        <ReferralModal
          partner={referring}
          direction="to_partner"
          onClose={() => setReferring(null)}
          onDone={async () => {
            setReferring(null);
            await referralsQuery.reload();
          }}
        />
      )}
    </div>
  );
}
